import Vue from 'vue'
import Router from 'vue-router'
import Layout from '@/views/Layout.vue'
import Login from '@/views/Login.vue'
import NotFound from '@/views/404.vue'
import { menuApi } from '@/api'
import { menuToRoutes } from '@/utils/route'
import { loadPermissionsFromStorage } from '@/utils/storage-permission'
import { sk } from '@/config'
import { getAccessToken, refreshSession } from '@/utils/request'

Vue.use(Router)

export const constantRoutes = [
  { path: '/login', component: Login, hidden: true },
  { path: '/404', component: NotFound, hidden: true }
]

const createRouter = () => new Router({
  mode: 'hash', // 临时改为 hash 模式，避免 404 问题
  scrollBehavior: () => ({ y: 0 }),
  routes: constantRoutes
})

const router = createRouter()

// 从路由树中查找第一个叶子路径（用于默认首页重定向）
function getFirstPath(routes) {
  if (!routes || !routes.length) return null
  for (const r of routes) {
    if (r.children && r.children.length) {
      const p = getFirstPath(r.children)
      if (p && p !== '/') return p
    } else if (r.path && r.path !== '/') {
      return r.path
    }
  }
  return null
}

// 重置路由（用于退出登录后清空动态路由）
export function resetRouter() {
  const newRouter = createRouter()
  router.matcher = newRouter.matcher
  dynamicAdded = false
  menuLoadingPromise = null // 同时清除菜单加载 Promise
}

// ==================== 动态路由管理 ====================
// 动态路由状态标记
let dynamicAdded = false
// 菜单加载 Promise，用于避免并发加载
let menuLoadingPromise = null

/**
 * 路由守卫 - 处理认证、权限加载和动态路由
 * 
 * 功能说明：
 * 1. 检查用户认证状态（token）
 * 2. 每次路由切换时重新加载权限数据（确保权限数据最新）
 * 3. 动态加载菜单并添加路由
 * 4. 处理登录后的跳转逻辑
 */
router.beforeEach(async (to, from, next) => {
  let token = getAccessToken()
  
  // ==================== 1. 认证检查 ====================
  // 新标签页或浏览器重启后没有访问令牌时，先用 HttpOnly Cookie 静默恢复。
  if (!token) {
    await refreshSession()
    token = getAccessToken()
    if (!token) {
      if (to.path === '/login') return next()
      return next({ path: '/login', query: { redirect: to.fullPath } })
    }
  }
  
  // ==================== 2. 登录页重定向处理 ====================
  // 如果有token且访问登录页，跳转到根路径
  // 注意：这个检查要在动态路由检查之前，避免死循环
  if (token && to.path === '/login') {
    // 如果动态路由已加载，直接跳转
    if (dynamicAdded) {
      return next('/')
    }
    // 如果动态路由未加载，等待加载完成后再跳转
    // 这样可以避免在菜单加载过程中重复触发导航
    if (menuLoadingPromise) {
      try {
        await menuLoadingPromise
        if (dynamicAdded) {
          return next('/')
        }
      } catch (e) {
        // 如果加载失败，继续执行
      }
    }
    // 如果菜单未加载，先标记需要跳转，然后开始加载
    return next('/')
  }
  
  // ==================== 3. 动态路由检查（优先检查，确保 URL 立即更新）====================
  // 如果动态路由已加载，立即通过，让 URL 立即更新
  // 这是关键优化：不等待任何异步操作，立即更新 URL
  // 注意：将动态路由检查放在权限加载之前，确保 URL 能够立即更新
  if (dynamicAdded) {
    // 权限数据在后台异步加载，不阻塞路由导航
    if (token) {
      // 使用 nextTick 让权限加载在路由导航之后执行，不阻塞 URL 更新
      Vue.nextTick(() => {
        loadPermissionsFromStorage()
      })
    }
    return next()
  }
  
  // ==================== 4. 权限数据加载 ====================
  // 每次路由切换时重新加载权限数据，确保权限数据是最新的
  // 注意：loadPermissionsFromStorage() 内部已经处理了所有错误，不会抛出异常
  // 权限数据会存储到 Vuex 的 permission 模块中，供 v-permission 指令和权限混入使用
  if (token) {
    loadPermissionsFromStorage()
  }
  
  // 如果菜单正在加载，等待加载完成（避免重复加载）
  // 注意：只有在动态路由未加载时才需要等待
  if (menuLoadingPromise) {
    try {
      await menuLoadingPromise
      // 加载完成后，重新检查 dynamicAdded
      if (dynamicAdded) {
        return next()
      }
    } catch (e) {
      // 如果之前的加载失败，继续执行新的加载
      menuLoadingPromise = null
    }
  }
  
  // ==================== 5. 登录后路由重置 ====================
  // 检查是否需要重置动态路由（当从登录页跳转时）
  // 注意：这个检查要在菜单加载检查之后，避免在加载过程中重复重置
  // 关键：如果菜单正在加载，不要重置状态，而是等待加载完成
  if (token && to.path !== '/login' && from.path === '/login') {
    // 从登录页跳转过来，需要重置动态路由状态，强制重新加载
    // 但是，如果菜单正在加载，不要重置，而是等待加载完成
    if (!menuLoadingPromise) {
      dynamicAdded = false
      menuLoadingPromise = null // 清除之前的加载 Promise
    }
  }
  
  // ==================== 6. 动态路由加载 ====================
  // 需要加载动态路由
  // 核心优化：先快速加载路由（使用缓存），立即 next() 让 URL 更新
  // 然后再异步更新路由（如果缓存过期）
  if (token && !dynamicAdded) {
    try {
      // 先尝试从缓存快速加载（同步操作，避免等待）
      let menus = []
      const cached = localStorage.getItem(sk('menus'))
      if (cached) {
        try {
          menus = JSON.parse(cached)
        } catch (e) {
          localStorage.removeItem(sk('menus'))
        }
      }
      
      // 如果有缓存，立即使用缓存，快速加载路由，然后立即 next()
      // 这样 URL 可以立即更新，不需要等待 API
      if (menus && menus.length > 0) {
        const children = menuToRoutes(menus)
        const defaultRedirect = getFirstPath(children) || '/404'
        const defaultRoute = { path: '/', redirect: defaultRedirect }
        
        // 在添加新路由前，先重置路由匹配器，避免重复添加路由
        const newRouter = createRouter()
        router.matcher = newRouter.matcher
        
        const routesToAdd = [
          { path: '/', component: Layout, children: [defaultRoute, ...children] },
          { path: '/404', component: NotFound, hidden: true },
          { path: '*', redirect: '/404', hidden: true }
        ]
        
        routesToAdd.forEach(route => {
          router.addRoute(route)
        })
        
        dynamicAdded = true
        
        // 先放行路由，让 URL 立即更新
        // 然后在后台异步更新菜单（如果缓存过期）
        Vue.nextTick(() => {
          // 后台异步更新菜单，不阻塞路由导航
          menuApi.getMenuData().then(res => {
            if (res.code === 200) {
              const newMenus = (res.data && res.data.jz) || res.data || []
              if (newMenus.length > 0) {
                localStorage.setItem(sk('menus'), JSON.stringify(newMenus))
                // 如果菜单有变化，可以在这里更新路由（可选）
              }
            }
          }).catch(() => {
            // 静默失败，使用缓存即可
          })
        })
        
        // 等待路由匹配完成（路由刚添加后，需要等待 Vue Router 重新匹配）
        await Vue.nextTick()
        
        // 如果当前路径是 '/' 或 '/404' 或没有匹配到路由，跳转到第一个菜单或 404
        if (to.path === '/' || to.path === '/404' || to.matched.length === 0) {
          const firstPath = getFirstPath(children)
          if (firstPath) {
            return next({ path: firstPath, replace: true })
          }
          return next({ path: '/404', replace: true })
        }
        // 立即 next()，让 URL 立即更新
        return next()
      }
      
      // 如果没有缓存，需要调用API（异步操作）
      // 开始加载菜单（使用 Promise 包装以避免并发）
      menuLoadingPromise = (async () => {
        try {
          // 设置超时时间为 10 秒，防止长时间等待
          const timeoutPromise = new Promise((_, reject) => {
            setTimeout(() => reject(new Error('菜单加载超时')), 10000)
          })
          
          const menuPromise = menuApi.getMenuData()
          const res = await Promise.race([menuPromise, timeoutPromise])
          
          menus = (res && res.data && (res.data.jz || res.data)) || []
          if (menus.length > 0) {
            localStorage.setItem(sk('menus'), JSON.stringify(menus))
          } else {
            // 如果API返回空数据，不要清除token，直接使用空菜单
            menus = []
          }
        } catch (e) {
          // API失败或超时时，尝试从用户信息中获取默认菜单
          try {
            // API 失败时不再注入默认菜单，仅使用空菜单
            menus = []
          } catch (parseError) {
            menus = []
          }
        }
        
        const children = menuToRoutes(menus)
        const defaultRedirect = getFirstPath(children) || '/404'
        const defaultRoute = { path: '/', redirect: defaultRedirect }
        
        // 在添加新路由前，先重置路由匹配器，避免重复添加路由
        // 这样可以防止出现 "Duplicate named routes definition" 警告
        const newRouter = createRouter()
        router.matcher = newRouter.matcher
        
        // 统一作为 Layout 的子路由注入，避免顶层重复 '/'
        // 按照官方文档：addRoute 只能添加单个路由，需要循环调用
        const routesToAdd = [
          { path: '/', component: Layout, children: [defaultRoute, ...children] },
          { path: '/404', component: NotFound, hidden: true },
          { path: '*', redirect: '/404', hidden: true }
        ]
        
        // 循环调用 addRoute 添加每个路由（addRoute 只能添加单个路由对象）
        routesToAdd.forEach(route => {
          router.addRoute(route)
        })
        
        dynamicAdded = true
        
        return { children, menus }
      })()
      
      // 等待菜单加载完成
      const result = await menuLoadingPromise
      menuLoadingPromise = null // 清除 Promise
      
      const { children } = result
      const firstPath = getFirstPath(children)
      
      // 如果当前路径是 '/' 或 '/404' 或没有匹配到路由，需要跳转
      if (to.path === '/' || to.path === '/404' || to.matched.length === 0) {
        // 如果找到了第一个有效路径，跳转到该路径
        if (firstPath) {
          return next({ path: firstPath, replace: true })
        }
        // 如果没有找到有效路径，跳转到 404
        await Vue.nextTick()
        return next({ path: '/404', replace: true })
      }
      // 如果当前路径已经匹配到路由，直接通过
      return next()
    } catch (e) {
      // ==================== 7. 错误处理 ====================
      // 如果菜单加载失败，尝试使用空菜单继续，避免卡死
      menuLoadingPromise = null // 清除 Promise，允许下次重试
      
      if (!dynamicAdded) {
        try {
          const children = menuToRoutes([])
          const defaultRoute = { path: '/', redirect: '/404' }
          
          // 在添加新路由前，先重置路由匹配器，避免重复添加路由
          const newRouter = createRouter()
          router.matcher = newRouter.matcher
          
          // 按照官方文档：addRoute 只能添加单个路由，需要循环调用
          const routesToAdd = [
            { path: '/', component: Layout, children: [defaultRoute, ...children] },
            { path: '/404', component: NotFound, hidden: true },
            { path: '*', redirect: '/404', hidden: true }
          ]
          
          // 循环调用 addRoute 添加每个路由
          routesToAdd.forEach(route => {
            router.addRoute(route)
          })
          
          dynamicAdded = true
          
          // 如果当前路径是 '/' 或 '/404' 或没有匹配到路由，跳转到默认首页
          // 使用 nextTick 确保路由已经添加完成后再导航
          await Vue.nextTick()
          if (to.path === '/' || to.path === '/404' || to.matched.length === 0) {
            return next({ path: '/', replace: true })
          }
          return next()
        } catch (err) {
          // 如果连空菜单都添加失败，跳转到登录页
          return next('/login')
        }
      }
      // 如果 dynamicAdded 已经是 true，说明路由已经添加过了，直接通过
      return next()
    }
  }
  
  // ==================== 8. 兜底处理 ====================
  // 如果执行到这里，说明有token但dynamicAdded为true（不应该发生），或者没有token（也不应该发生）
  // 为了安全，直接通过
  return next()
})

// ==================== 9. 重写 router.push 方法，忽略重复导航错误 ====================
// 解决「重复导航 / 路由解析异常」问题
// 如果菜单点击时触发了多次 router.push, Vue Router 会取消重复导航
// 导致「点击后没反应,等很久才跳转」,地址栏也不变
// 重写 push 方法，只忽略「重复导航」错误，其他错误正常抛出
const originalPush = router.push
router.push = function push(location) {
  return originalPush.call(this, location).catch(err => {
    // 只忽略「重复导航」错误，其他错误正常抛出
    if (err.name !== 'NavigationDuplicated') {
      throw err
    }
  })
}

export default router
